import { Badge, Button } from '@/components/ui'
import { formatElapsedTime, formatTimestampShort } from '@/lib/timeFormatters'
import { cn } from '@/lib/utils'
import type { ChainRunLink, ChainRunLinkStatus } from '@/types'
import { triggerOnLabel } from '@/types'
import { ChevronDown, ChevronRight, ExternalLink } from 'lucide-react'
import { WorkflowStatusBadge } from '../../workflows/shared'

interface Props {
  link: ChainRunLink
  isExpanded: boolean
  isWaiting: boolean
  waitingText?: string | null
  onSelect: () => void
  onOpenRun: (workflowRunId: string) => void
}

const linkStatusBorderClasses: Record<ChainRunLinkStatus, string> = {
  Completed: '!border-success-foreground',
  Triggered: '!border-info-foreground',
  Failed: '!border-danger-foreground',
  Pending: '!border-neutral-foreground',
  Skipped: '!border-neutral-foreground',
  Cancelled: '!border-cancelled-foreground',
}

export function ChainLinkCard({ link, isExpanded, isWaiting, waitingText, onSelect, onOpenRun }: Props) {
  const Chevron = isExpanded ? ChevronDown : ChevronRight
  const workflowRunId = link.run?.workflowRunId ?? link.workflowRunId
  const duration = link.run?.startedAt ? formatElapsedTime(link.run.startedAt, link.run.completedAt) : null
  const badgeStatus = link.status === 'Triggered' ? link.run?.status ?? 'Triggered' : link.status

  return (
    <div
      className={cn(
        'border-2 rounded-lg overflow-hidden bg-card',
        isWaiting ? '!border-warning-foreground' : linkStatusBorderClasses[link.status],
      )}
      data-testid="chain-link-card"
    >
      <button
        onClick={onSelect}
        aria-label={`Select link ${link.linkName}`}
        aria-expanded={isExpanded}
        className="flex items-center gap-2 px-4 py-3 w-full text-left transition-colors hover:bg-muted/50"
      >
        <Chevron className="h-4 w-4 shrink-0 text-muted-foreground" />
        <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center text-primary-foreground text-xs font-semibold shrink-0">
          {link.position}
        </div>
        <div className="min-w-0">
          <div className="font-semibold truncate">{link.linkName}</div>
          <div className="text-xs text-muted-foreground truncate">{link.workflowDisplayName}</div>
        </div>
        {duration && <span className="text-sm text-muted-foreground shrink-0">{duration}</span>}
        <div className="ml-auto flex items-center gap-2 shrink-0">
          <Badge variant="outline" className="text-xs">{triggerOnLabel(link.triggerOn)}</Badge>
          <WorkflowStatusBadge status={badgeStatus} />
        </div>
      </button>

      {isExpanded && (
        <div className="p-4">
          <div className="flex gap-4 text-[11px] text-muted-foreground mb-3 pb-2 border-b">
            <span>Started: {formatTimestampShort(link.run?.startedAt ?? null)}</span>
            <span>Completed: {formatTimestampShort(link.run?.completedAt ?? null)}</span>
          </div>
          {workflowRunId ? (
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium">Run {workflowRunId.slice(0, 8)}</span>
              <Button
                size="sm"
                variant="outline"
                className="ml-auto bg-toolbar border-border-accent"
                aria-label={`Open run of link ${link.linkName}`}
                onClick={() => onOpenRun(workflowRunId)}
              >
                <ExternalLink className="h-3 w-3 mr-1" /> Open run
              </Button>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">{waitingText ?? 'Not started'}</p>
          )}
        </div>
      )}
    </div>
  )
}
